import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { AdminGamic } from './types';
import RemoteFile from '@/helper/RemoteFile';
import { updateGamicLinks } from '@/store/Gamic';

interface Props {
  gamic: AdminGamic;
}

// WL-DL-V1: admin can now edit iOS / Android store URLs after approval
// via PATCH /gamic/links/:id. The backend helper handles wishlist user
// notification + dedup. The save here does NOT reset the gamic's status.
const GamicDetails: React.FC<Props> = ({ gamic }) => {
  const dispatch = useDispatch<any>();
  const [ios, setIos] = useState(gamic.iosDownloadUrl || '');
  const [android, setAndroid] = useState(gamic.androidDownloadUrl || '');
  const [saving, setSaving] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  useEffect(() => {
    setIos(gamic.iosDownloadUrl || '');
    setAndroid(gamic.androidDownloadUrl || '');
    setLinkError(null);
  }, [gamic.id, gamic.iosDownloadUrl, gamic.androidDownloadUrl]);

  const handleSave = async () => {
    const iosTrim = ios.trim();
    const androidTrim = android.trim();
    if (iosTrim && !iosTrim.toLowerCase().includes('apps.apple.com')) {
      setLinkError('iOS link must be an apps.apple.com URL');
      return;
    }
    if (androidTrim && !androidTrim.toLowerCase().includes('play.google.com')) {
      setLinkError('Android link must be a play.google.com URL');
      return;
    }
    setLinkError(null);
    setSaving(true);
    try {
      await dispatch(
        updateGamicLinks({
          gamicId: gamic.id,
          iosDownloadUrl: iosTrim,
          androidDownloadUrl: androidTrim,
        }),
      );
    } catch (e) {
      toast.error('Failed to save links');
    } finally {
      setSaving(false);
    }
  };

  const dirty =
    ios.trim() !== (gamic.iosDownloadUrl || '').trim() ||
    android.trim() !== (gamic.androidDownloadUrl || '').trim();

  return (
    <div className='space-y-6 w-full'>
      {/* Game File */}
      <div className='border-b py-2 '>
        <h4 className='text-xs text-gray-500 uppercase'>Game File</h4>
        <p className='mt-1 text-sm text-gray-700'>{gamic.file}</p>
      </div>

      {/* Name */}
      <div className='border-b py-2 '>
        <h4 className='text-xs text-gray-500 uppercase'>Name</h4>
        <p className='mt-1 text-sm text-gray-700'>{gamic.title}</p>
      </div>

      {/* WL-DL-V1: split download links — admin editable */}
      <div className='border-b py-2 space-y-2'>
        <h4 className='text-xs text-gray-500 uppercase'>Download Links</h4>

        <div>
          <label className='block text-[11px] text-gray-500 mb-1'>
            App Store URL (iOS)
          </label>
          <input
            type='url'
            value={ios}
            onChange={(e) => setIos(e.target.value)}
            placeholder='https://apps.apple.com/app/...'
            className='w-full bg-[#F5F5F5] text-sm px-3 py-2 rounded border border-[#E3E3E3] focus:outline-none focus:border-[#FF0050]'
          />
        </div>

        <div>
          <label className='block text-[11px] text-gray-500 mb-1'>
            Google Play URL (Android)
          </label>
          <input
            type='url'
            value={android}
            onChange={(e) => setAndroid(e.target.value)}
            placeholder='https://play.google.com/store/apps/details?id=...'
            className='w-full bg-[#F5F5F5] text-sm px-3 py-2 rounded border border-[#E3E3E3] focus:outline-none focus:border-[#FF0050]'
          />
        </div>

        {linkError && <p className='text-xs text-red-600'>{linkError}</p>}

        <div className='flex items-center gap-2 pt-1'>
          <button
            type='button'
            onClick={handleSave}
            disabled={!dirty || saving}
            className='px-4 py-1.5 text-sm rounded bg-[#FF0050] text-white disabled:bg-gray-300 disabled:cursor-not-allowed'
          >
            {saving ? 'Saving…' : 'Save links'}
          </button>
          <span className='text-xs text-gray-400'>
            Saving notifies wishlist users on the matching platform. Status is
            preserved.
          </span>
        </div>

        {gamic.downloadLink && !ios && !android && (
          <p className='text-[11px] text-gray-400 mt-1'>
            Legacy single link on file: {gamic.downloadLink}
          </p>
        )}
      </div>

      <div className='border-b py-2 '>
        <h4 className='text-xs text-gray-500 uppercase'>Description</h4>
        <p className='mt-1 text-sm text-gray-700 leading-relaxed'>
          {gamic.description || 'No description available.'}
        </p>
      </div>

      {/* ─── Tags */}
      <div className='border-b py-2 '>
        <h4 className='text-xs text-gray-500 uppercase'>Tags</h4>
        <div className='mt-2 flex flex-wrap gapy-2'>
          {(Array.isArray(gamic.tags)
            ? gamic.tags
            : String(gamic.tags).split(',')
          )
            .filter(Boolean)
            .map((t, i) => (
              <span
                key={i}
                className='px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full'
              >
                {t.trim()}
              </span>
            ))}
        </div>
      </div>

      {/* ─── Category */}
      <div className='border-b py-2 '>
        <h4 className='text-xs text-gray-500 uppercase'>Category</h4>
        <p className='mt-1 text-sm text-gray-700'>{gamic.category}</p>
      </div>

      {/* ─── Thumbnail */}
      <div className='flex flex-col border-b '>
        <h4 className='text-xs text-gray-500 uppercase'>Thumbnail</h4>
        <RemoteFile
          className='w-[391px] self-center'
          keyPath={gamic.img}
          key={gamic.title}
        />
      </div>
    </div>
  );
};

export default GamicDetails;
